<template>
  <div>
    <v-radio-group
      class="mb-6"
      :error-messages="errorMessages"
      :hide-details="!errorMessages.length"
      :class="[$style['radio-group'], gridFourItems ? $style['radio-group-four'] : '']"
      row
      v-bind="{ ...$attrs, ...$props }"
      @change="emitRadioGroupChange"
    >
      <v-radio
        v-for="(item, index) in items"
        :key="index"
        :value="item.value"
        :required="required"
        :class="{
          [$style['radio-item--omitted']]: $attrs.value && $attrs.value !== item.value,
        }"
        @click="() => emitRadioClick(item.value)"
      >
        <template #label>
          <v-card
            :class="$style['radio-item']"
            :outlined="$attrs.value === item.value"
            :disabled="$attrs.value !== item.value"
            flat
            tile
          >
            <div :class="$style['card__content']">
              <BaseImage v-if="item.image" :src="item.image.imageUrl" :alt="item.image.name" />

              <slot name="card-content" :item="item" />
            </div>

            <IconSelect
              :class="$style['radio-item__chevron-icon']"
              icon="chevronUp"
              :icon-props="{ 'stroke-width': 1 }"
              height="44"
              width="44"
            />
          </v-card>
        </template>
      </v-radio>
    </v-radio-group>

    <BaseAlert
      v-if="computedAlertMessage && !$attrs.value"
      type="info"
      alignment="md-start"
      :message="computedAlertMessage"
    />
  </div>
</template>

<script>
import IconSelect from '@/js/components/Shared/IconSelect/IconSelect.vue'

export default {
  components: {
    IconSelect,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    required: {
      type: Boolean,
      default: true,
    },
    errorMessages: {
      type: Array,
      default: () => [],
    },
    alertMessage: {
      type: String,
      default: null,
    },
    gridFourItems: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    computedAlertMessage() {
      return this.alertMessage ?? this.$t('complaintForm.ticket.select_model_type')
    },
  },
  methods: {
    emitRadioGroupChange(value) {
      this.$emit('input', value)
    },
    emitRadioClick(value) {
      this.$emit('tab-click', value)
    },
  },
}
</script>

<style lang="scss" module>
$desktopViewBreakpoint: map-get($grid-breakpoints, 'md');
@import '~@/styles/utilities/_hide.scss';

.radio-group {
  // --- Desktop styles --- //
  @media screen and (min-width: '#{$desktopViewBreakpoint}') {
    :global(.v-input--radio-group__input) {
      position: relative;

      display: grid;
      grid-gap: 1.875rem;
      grid-template-rows: 1fr;
      grid-template-columns: repeat(3, 1fr);
      justify-items: center;

      &::before {
        position: absolute;
        right: 50%;
        bottom: 0;
        left: 50%;
        z-index: 1;

        width: 100vw;

        height: 1px;
        margin-right: -50vw;
        margin-left: -50vw;

        background: var(--primary);

        content: '';
      }

      > * {
        width: 100% !important;
      }
    }
  }

  // --- Mobile styles --- //
  @media screen and (max-width: '#{$desktopViewBreakpoint - 1px}') {
    :global(.v-radio) {
      padding: 1rem;

      border: 1px solid currentColor;
      border-radius: 1.25rem;
    }

    :global(.v-input--radio-group__input) {
      @media screen and (min-width: 425px) {
        grid-template-columns: repeat(auto-fit, minmax(18rem, 1fr));
      }
      display: grid;
      grid-auto-rows: minmax(7.5rem, max-content);
      grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
      gap: 1rem;
    }

    :global(.v-item--active) {
      .radio-item {
        direction: rtl;
      }

      .radio-item__chevron-icon {
        position: relative;
        left: -0.5rem;

        transform: rotate(-90deg);
      }
    }
  }

  // --- Common styles --- //
  color: var(--primary) !important;

  :global(.v-input--selection-controls__input) {
    // Hide controls (circle)
    @extend %visually-hidden;
  }

  :global(.v-radio) {
    @media screen and (min-width: 664px) {
      max-width: 25rem;
    }

    @media screen and (min-width: 725px) {
      max-width: 28rem;
    }
    z-index: 0;

    width: 100%;

    margin: 0 !important;
  }

  :global(.v-label) {
    width: 100% !important;
    height: 100% !important;
    margin: 0;

    padding-left: 0 !important;

    background: var(--white);

    &::before {
      content: none !important;
    }
  }

  :global(.v-item--active) {
    z-index: 1;
  }
}

.radio-group-four {
  // --- Desktop styles --- //
  @media screen and (min-width: '#{$desktopViewBreakpoint}') {
    :global(.v-input--radio-group__input) {
      grid-template-columns: repeat(4, 1fr);
    }
  }

  // --- Mobile styles --- //
  @media screen and (max-width: '#{$desktopViewBreakpoint - 1px}') {
    :global(.v-input--radio-group__input) {
      @media screen and (min-width: 425px) {
        grid-template-columns: repeat(auto-fit, minmax(18rem, 1fr));
      }

      grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
    }
  }
}

.radio-item {
  // --- Desktop styles --- //
  @media screen and (min-width: '#{$desktopViewBreakpoint}') {
    &:global(.v-card) {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      padding: 1.25rem 1rem;

      border-color: var(--primary) !important;
      border-bottom: none !important;
      border-radius: 1.25rem !important;
      border-bottom-right-radius: 0 !important;
      border-bottom-left-radius: 0 !important;

      img {
        max-height: 2.875rem;
      }
    }
  }

  // --- Mobile styles --- //
  @media screen and (max-width: '#{$desktopViewBreakpoint - 1px}') {
    &:global(.v-card) {
      display: grid;

      grid-template-columns: minmax(0, 16rem) min-content;
      align-items: center;
      justify-content: space-between;
      width: 100%;

      border: none !important;

      img {
        max-height: 5rem;
      }
    }
  }
  // --- Common styles --- //

  &:global(.v-card) {
    img {
      flex: auto;
      width: 100%;
      max-width: 100%;
      height: 100%;
      object-fit: contain;
      object-position: center;
    }
  }

  // -- Attribute/Element styles --- //

  &--omitted {
    @media screen and (max-width: '#{$desktopViewBreakpoint - 1px}') {
      @include visually-hidden;
    }
  }

  &__chevron-icon {
    @media screen and (min-width: '#{$desktopViewBreakpoint}') {
      display: none;
    }
    transform: rotate(90deg);
  }
}

.card {
  &__content {
    position: relative;

    display: flex;
    align-items: center;
    justify-content: space-evenly;
  }
}
</style>
